import { createSlice } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';
import { RootState } from 'store';
import _ from 'lodash';

export type FormSectionErrorType = {
    section: string;
    field: {
        name: string;
        error: boolean;
    };
};

export interface FormDetails {
    formSections: string[];
    currentFormSectionIndex: number;
    formSectionErrors: FormSectionErrorType[];
    isFormSubmitDisabled: boolean;
}

const initialState: FormDetails = {
    formSections: [],
    currentFormSectionIndex: 0,
    formSectionErrors: [],
    isFormSubmitDisabled: false,
};

export const formSlice = createSlice({
    name: 'form',
    initialState,
    reducers: {
        addFormSection: (state, action: PayloadAction<string>) => {
            state.formSections.push(action.payload);
        },
        setFormSections: (state, action: PayloadAction<string[]>) => {
            state.formSections = action.payload;
        },
        setCurrentSectionIndex: (state, action: PayloadAction<number>) => {
            state.currentFormSectionIndex = action.payload;
        },
        setFormSectionError: (
            state,
            action: PayloadAction<FormSectionErrorType>
        ) => {
            const payload = action.payload;

            // prevent duplicate form section errors to populate the array from rerender side effects.
            const filterFormSectionErrors = (s: FormSectionErrorType) =>
                !_.isEqual(s, payload);

            state.formSectionErrors = [
                ...state.formSectionErrors.filter(filterFormSectionErrors),
                payload,
            ];
        },
        resetFormState: (_) => initialState,
        setIsFormSubmitDisabled: (state, action: PayloadAction<boolean>) => {
            state.isFormSubmitDisabled = action.payload;
        },
    },
});

export const {
    addFormSection,
    setFormSections,
    setCurrentSectionIndex,
    resetFormState,
    setFormSectionError,
    setIsFormSubmitDisabled,
} = formSlice.actions;

export default formSlice.reducer;

export const useFormStateSelector = (state: RootState) => state.form;
